import { Link } from "react-router-dom";
import { AtSign, Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { SunkevLogo } from "@/components/Logo";
import { useContent } from "@/context/ContentContext";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/craftsi", label: "Craftsi" },
  { to: "/businesses", label: "Our Businesses" },
  { to: "/careers", label: "Careers" },
  { to: "/news", label: "News & Updates" },
  { to: "/contact", label: "Contact Us" },
];

const PLATFORM_LINKS = [
  { to: "/craftsi", label: "Craftsi Customer App" },
  { to: "/craftsi", label: "Craftsi Vendor App" },
];

const LEGAL_LINKS = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
];

const Footer = () => {
  const content = useContent();
  return (
  <footer data-testid="site-footer" className="bg-navy-900 text-white">
    <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
      <div className="grid gap-14 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SunkevLogo dark />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70">
            {content.footer_description ||
              "Building technology-driven digital products and solutions that connect businesses, service providers and customers."}
          </p>
          <p className="mt-4 max-w-sm text-sm italic leading-relaxed text-white/50">
            {content.footer_tagline || "Innovating today for a smarter tomorrow."}
          </p>
          <div className="mt-8 space-y-6">
            {[
              {
                group: "Sunkev Enterprises",
                links: [
                  { Icon: Instagram, label: "Sunkev Enterprises on Instagram", slug: "instagram-sunkev", href: "https://www.instagram.com/sunkev_enterprises_pvt_ltd" },
                ],
              },
              {
                group: "Craftsi",
                links: [
                  { Icon: Instagram, label: "Craftsi on Instagram", slug: "instagram-craftsi", href: "https://www.instagram.com/craftsi.in" },
                  { Icon: Facebook, label: "Craftsi on Facebook", slug: "facebook-craftsi", href: "https://www.facebook.com/share/1DgnB5X1dq/" },
                  { Icon: Twitter, label: "Craftsi on X", slug: "x-craftsi", href: "https://x.com/craftsiindia" },
                  { Icon: AtSign, label: "Craftsi on Threads", slug: "threads-craftsi", href: "https://www.threads.com/@craftsi.in" },
                ],
              },
            ].map(({ group, links }) => (
              <div key={group}>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-gold">
                  {group}
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {links.map(({ Icon, label, slug, href }) => (
                    <a
                      key={slug}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                      data-testid={`social-${slug}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:border-gold hover:text-gold"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-gold">
            Quick Links
          </h3>
          <ul className="mt-6 space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  className="text-sm text-white/70 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-gold">
            Platform
          </h3>
          <ul className="mt-6 space-y-3">
            {PLATFORM_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  className="text-sm text-white/70 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="mt-10 font-heading text-sm font-bold uppercase tracking-[0.2em] text-gold">
            Legal
          </h3>
          <ul className="mt-6 space-y-3">
            {LEGAL_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-${l.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="text-sm text-white/70 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-gold">
            Contact
          </h3>
          <ul className="mt-6 space-y-4 text-sm text-white/70">
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a href="mailto:sunkeventerprises@gmail.com" className="transition-colors duration-300 hover:text-white">
                sunkeventerprises@gmail.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a href="tel:+919392481929" className="transition-colors duration-300 hover:text-white">
                +91 93924 81929
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>
                19-3-1A/S9, Postal Colony, Korrameenugunta,
                <br />
                Renigunta Rd, Tirupati – 517501,
                <br />
                Andhra Pradesh, India
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
        <p className="text-xs text-white/50">
          © 2026 Sunkev Enterprises Private Limited. All rights reserved.
        </p>
        <p className="text-xs text-white/40">
          Craftsi is a product of Sunkev Enterprises Private Limited.
        </p>
        <p className="text-xs text-white/40">
          CIN: U47912AP2023PTC110487 · Incorporated on 11 March 2023
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
